import React, { FC, useState, useEffect, useContext, useRef } from 'react';
import { Button, styled } from '@grupoboticario/flora-react';

import { actions, CatalogContext } from 'src/contexts/Catalog';
import {
  goToNextPage,
  goToPreviousPage,
  zoomIn,
} from 'src/contexts/Catalog/store/actions';

import { DesktopSlide } from './DesktopSlide';

import { shouldGoTo } from './DesktopSliderServices';
import { ChevronLeft, ChevronRight } from '@material-ui/icons';
import { sendCatalogEvent } from 'src/services/googleAnalytics/helpers/catalog';

export type UpdateCatalogData = (
  position: number,
  secondaryPosition?: number,
) => Promise<void>;

const SliderWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
  height: 'calc(100vh - 140px)',
  width: '100%',
  position: 'relative',

  variants: {
    navigation: {
      inside: {
        button: {
          '&.left': {
            left: 0,
          },
          '&.right': {
            right: 0,
          },
        },
      },
      outside: {
        button: {
          '&.left': {
            left: -50,
          },
          '&.right': {
            right: -50,
          },
        },
      },
    },
  },

  button: {
    position: 'absolute',
    backgroundColor: 'transparent',
    color: '$dark-light',
    padding: '0',
    height: 'auto',

    svg: {
      width: '2em',
      height: '2em',
    },
  },
});

const StyledSlider = styled('div', {
  overflow: 'hidden',
  width: '100%',
  height: '100%',
  cursor: 'zoom-in',

  '&.grabbing': {
    cursor: 'grabbing',
  },
});

const StyledSlidesWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  height: '100%',
  justifyContent: 'flex-end',
});

interface IDesktopSlider {
  navigation?: 'outside' | 'inside';
}

export const DesktopSlider: FC<IDesktopSlider> = ({
  navigation = 'inside',
}) => {
  const [state, dispatch] = useContext(CatalogContext);
  const [slideWidth, setSlideWidth] = useState(0);

  let touchStartXPosition: number;

  const slider = useRef<HTMLDivElement>(null);

  const setSliderPosition = () => {
    const currentSlideWidth = (slider?.current?.clientWidth || 0) / 2;

    setSlideWidth(currentSlideWidth);
  };

  useEffect(() => {
    setSliderPosition();

    const currentSlider = slider?.current;

    currentSlider?.addEventListener('mousedown', handleMouseDown);
    window?.addEventListener('resize', setSliderPosition);

    return () => {
      currentSlider?.removeEventListener('mousedown', handleMouseDown);
      window?.removeEventListener('resize', setSliderPosition);
    };
  }, [state.page]);

  useEffect(() => {
    setSliderPosition();
  }, [state.isZoomActive]);

  const handleMouseMove = (e: MouseEvent) => {
    e.preventDefault();
    slider?.current?.classList.add('grabbing');
  };

  const handleMouseUp = (e: MouseEvent) => {
    const touch = {
      endX: e.clientX,
      startX: touchStartXPosition,
    };

    const sliderInfo = {
      currentPage: state.page,
      numberOfPages: state.numberOfPages,
    };

    slider?.current?.classList.remove('grabbing');

    const sliderWidth = slideWidth * 2;

    const goTo = shouldGoTo(touch, sliderInfo, sliderWidth);

    switch (goTo) {
      case 'nextPage':
        sendCatalogEvent.swipeNavigationNext();
        dispatch(goToNextPage());
        break;
      case 'previousPage':
        sendCatalogEvent.swipeNavigationPrev();
        dispatch(goToPreviousPage());
        break;
      case 'zoomMode':
        dispatch(zoomIn());
        sendCatalogEvent.pageClickZoomIn();
        break;
      default:
        break;
    }

    slider?.current?.removeEventListener('mousemove', handleMouseMove);
    slider?.current?.removeEventListener('mouseup', handleMouseUp);
    slider?.current?.removeEventListener('mouseleave', handleMouseUp);
    slider?.current?.addEventListener('mousedown', handleMouseDown);
  };

  const handleMouseDown = (e: MouseEvent) => {
    touchStartXPosition = e.clientX;

    slider?.current?.addEventListener('mousemove', handleMouseMove);
    slider?.current?.addEventListener('mouseup', handleMouseUp);
    slider?.current?.addEventListener('mouseleave', handleMouseUp);
    slider?.current?.removeEventListener('mousedown', handleMouseDown);
  };

  const handleNextPageClick = () => {
    dispatch(actions.goToNextPage());
    sendCatalogEvent.buttonClickNext();
  };

  const handlePreviousPageClick = () => {
    dispatch(actions.goToPreviousPage());
    sendCatalogEvent.buttonClickPrev();
  };

  return (
    <SliderWrapper navigation={navigation}>
      <StyledSlider ref={slider} id="slider" data-testid="slider">
        <StyledSlidesWrapper id="slides-wrapper" style={{}}>
          {state.exhibition?.currentPagesData.map((page) => (
            <DesktopSlide
              key={page.number}
              page={page}
              pageWidth={slideWidth}
              item="current"
              hasSpinner
            />
          ))}
        </StyledSlidesWrapper>
      </StyledSlider>
      <Button
        variant="alt"
        leftIcon={<ChevronLeft />}
        onClick={handlePreviousPageClick}
        label="Ir para página anterior"
        aria-label="Ir para página anterior"
        data-testid="go-to-previous-page-button"
        className="left"
        disabled={state.page === 1}
      ></Button>
      <Button
        variant="alt"
        leftIcon={<ChevronRight />}
        onClick={handleNextPageClick}
        label="Ir para próxima página"
        aria-label="Ir para próxima página"
        data-testid="go-to-next-page-button"
        className="right"
        disabled={state.page === state.numberOfPages}
      ></Button>
    </SliderWrapper>
  );
};
